import { useState, useEffect, useRef, memo } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useApp } from '../../../contexts/AppContext';
import { Unlink, Clock, Calendar, Monitor, AlertTriangle, MoreVertical, Library } from 'lucide-react';
import { Card, CardHeader, CardContent } from '../../ui/Card';
import Button from '../../ui/Button';
import Modal from '../../ui/Modal';
import { useToast } from '../../ui/Toast';
import { haptics } from '../../../utils/haptics';
import StationLibraryEditor from './StationLibraryEditor';
import { describeLibraryLine } from '../../../utils/stationLibrary';
import { TONE_CLASSES } from '../../../utils/tones';

const POPOVER_MOTION = {
  initial: { opacity: 0, y: -8, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -8, scale: 0.98 },
  transition: { duration: 0.15, ease: [0.4, 0, 0.2, 1] },
};

/**
 * Agent Card Component - Redesigned
 * Modern card with status indicators and animations
 */
const AgentCard = memo(function AgentCard({ agent, libraryEntry, libraryStatus }) {
  const { unlinkAgent, setStationLibrary } = useApp();
  const toast = useToast();
  const [isUnlinking, setIsUnlinking] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const popoverRef = useRef(null);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const menuItemRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    menuItemRef.current?.focus();
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  const openEditor = () => {
    setMenuOpen(false);
    setEditorOpen(true);
  };

  const handleSaveLibrary = async (agentId, name) => {
    try {
      await setStationLibrary(agentId, name);
    } catch (error) {
      toast.error(`Couldn't save the library: ${error.message}`);
      throw error;
    }
  };

  useEffect(() => {
    if (!showConfirm) return;
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        setShowConfirm(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setShowConfirm(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [showConfirm]);

  const handleUnlink = async () => {
    setIsUnlinking(true);
    try {
      await unlinkAgent(agent.agent_id);
      toast.success('Media Station unlinked successfully');
      haptics.success();
      setShowConfirm(false);
    } catch (error) {
      toast.error(`Failed to unlink Media Station: ${error.message}`);
      haptics.error();
    } finally {
      setIsUnlinking(false);
    }
  };

  const isOnline = agent.status === 'online';
  const libraryLine = describeLibraryLine(libraryEntry, libraryStatus);
  // The library is set once and rarely revisited, so the card face only
  // mentions it when the station is not using what was chosen here.
  const libraryNeedsAttention = libraryLine?.tone === 'warning' || libraryLine?.tone === 'error';
  const menuId = `station-menu-${agent.agent_id}`;

  return (
    <>
      <Card 
        interactive
        variant="elevated"
        data-testid="station-card"
        data-agent-id={agent.agent_id}
        className={isOnline ? 'ring-2 ring-emerald-500/20 shadow-glow' : ''}
      >
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`p-2 rounded-lg ${
                isOnline
                  ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500'
              }`}>
                <Monitor className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-body">
                  {agent.description || 'Media Station'}
                </h3>
                <p className="text-caption text-gray-500 dark:text-gray-400 font-mono mt-0.5 truncate">
                  {agent.agent_id.slice(0, 16)}...
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Status Badge */}
              <motion.div
                animate={{
                  scale: isOnline ? [1, 1.05, 1] : 1,
                }}
                transition={{
                  duration: 2,
                  repeat: isOnline ? Infinity : 0,
                  repeatType: "reverse",
                }}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-caption font-medium ${
                  isOnline 
                    ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400' 
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-400'
                }`}
              >
                <span className={`w-2 h-2 rounded-full ${
                  isOnline ? 'bg-emerald-500 animate-pulse' : 'bg-gray-400'
                }`} />
                {isOnline ? 'Online' : 'Offline'}
              </motion.div>

              {/* Unlink Button + Confirm Popover */}
              <div className="relative" ref={popoverRef}>
                <button
                  onClick={() => setShowConfirm((v) => !v)}
                  className={`p-1.5 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                    showConfirm
                      ? 'text-red-500 bg-red-50 dark:bg-red-900/20'
                      : 'text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20'
                  }`}
                  title="Unlink Media Station"
                  aria-haspopup="dialog"
                  aria-expanded={showConfirm}
                >
                  <Unlink className="w-4 h-4" />
                </button>

                <AnimatePresence>
                  {showConfirm && (
                    <motion.div
                      initial={{ opacity: 0, y: -8, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -8, scale: 0.98 }}
                      transition={{ duration: 0.15, ease: [0.4, 0, 0.2, 1] }}
                      role="dialog"
                      aria-label="Confirm unlink"
                      className="absolute top-full right-0 mt-2 z-50 w-72 max-w-[calc(100vw-2rem)] rounded-2xl bg-white dark:bg-elevated-dark-1 border border-gray-200 dark:border-white/10 shadow-soft-lg p-4"
                    >
                      <div className="flex items-start gap-3 mb-3">
                        <div className="flex-shrink-0 p-1.5 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                          <AlertTriangle className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-body-sm font-semibold text-gray-900 dark:text-gray-100">
                            Unlink this Media Station?
                          </p>
                          <p className="text-caption text-gray-600 dark:text-gray-400 mt-1">
                            You'll need to link it again to use it.
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={() => setShowConfirm(false)}
                          variant="secondary"
                          size="sm"
                          className="flex-1"
                          disabled={isUnlinking}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleUnlink}
                          variant="danger"
                          size="sm"
                          className="flex-1"
                          loading={isUnlinking}
                          disabled={isUnlinking}
                        >
                          Unlink
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Station menu */}
              <div className="relative" ref={menuRef}>
                <button
                  ref={menuButtonRef}
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  className={`p-1.5 rounded-lg transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 ${
                    menuOpen
                      ? 'text-gray-900 dark:text-gray-100 bg-gray-100 dark:bg-white/10'
                      : 'text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10'
                  }`}
                  aria-label={`More actions for ${agent.description || 'Media Station'}`}
                  aria-haspopup="menu"
                  aria-expanded={menuOpen}
                  aria-controls={menuOpen ? menuId : undefined}
                  data-testid="station-menu"
                >
                  <MoreVertical className="w-4 h-4" aria-hidden="true" />
                </button>

                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      {...POPOVER_MOTION}
                      id={menuId}
                      role="menu"
                      aria-label={`${agent.description || 'Media Station'} actions`}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape' || e.key === 'Tab') {
                          e.preventDefault();
                          closeMenu();
                        }
                      }}
                      className="absolute top-full right-0 mt-2 z-50 w-64 max-w-[calc(100vw-2rem)] rounded-2xl bg-white dark:bg-elevated-dark-1 border border-gray-200 dark:border-white/10 shadow-soft-lg overflow-hidden py-1"
                    >
                      <button
                        ref={menuItemRef}
                        type="button"
                        role="menuitem"
                        onClick={openEditor}
                        data-testid="station-menu-library"
                        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-100 dark:hover:bg-white/5 focus:bg-gray-100 dark:focus:bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-amber-500 transition-colors"
                      >
                        <Library className="w-4 h-4 mt-0.5 flex-shrink-0 text-indigo-600 dark:text-indigo-400" aria-hidden="true" />
                        <span className="min-w-0">
                          <span className="block text-body-sm font-medium text-gray-900 dark:text-gray-100">
                            ProPresenter library…
                          </span>
                          {libraryLine && (
                            <span
                              data-testid="station-menu-library-current"
                              data-tone={libraryLine.tone}
                              className={`block text-caption mt-0.5 break-words ${
                                libraryLine.tone === 'success' ? 'text-gray-500 dark:text-gray-400' : TONE_CLASSES[libraryLine.tone]
                              }`}
                            >
                              {libraryLine.text}
                            </span>
                          )}
                        </span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </CardHeader>

        <CardContent>
          {libraryNeedsAttention && (
            <p
              data-testid="station-library-attention"
              data-tone={libraryLine.tone}
              className={`flex items-start gap-2 mb-3 text-body-sm ${TONE_CLASSES[libraryLine.tone]}`}
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden="true" />
              <span className="min-w-0 break-words">{libraryLine.text}</span>
            </p>
          )}

          <div className="space-y-3 text-body-sm">
            {agent.last_seen && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Clock className="w-4 h-4" />
                <span>Last seen: {new Date(agent.last_seen).toLocaleString()}</span>
              </div>
            )}
            {agent.linked_at && (
              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                <Calendar className="w-4 h-4" />
                <span>Linked: {new Date(agent.linked_at).toLocaleDateString()}</span>
              </div>
            )}
          </div>

        </CardContent>

      </Card>

      {createPortal(
        <Modal
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          title="ProPresenter library"
          closeLabel="Close library settings"
          returnFocusRef={menuButtonRef}
          testId="station-library-dialog"
          size="md"
        >
          <StationLibraryEditor
            agent={agent}
            entry={libraryEntry}
            settingsStatus={libraryStatus}
            onSave={handleSaveLibrary}
          />
        </Modal>,
        document.body,
      )}
    </>
  );
});

export default AgentCard;
